'use client';

import React from 'react';
import { motion } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';

interface GalleryHeroProps {
  totalPhotos: number;
  totalVideos: number;
  totalStories: number;
}

const GalleryHero: React.FC<GalleryHeroProps> = ({ totalPhotos, totalVideos, totalStories }) => {
  return (
    <section className="relative bg-gradient-to-br from-primary/10 via-secondary/5 to-accent/10 py-10 sm:py-16 lg:py-24">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6"
          >
            <Icon name="PhotoIcon" size={20} />
            <span className="text-sm font-semibold">Visual Stories of Impact</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-4xl lg:text-5xl font-heading font-bold text-foreground mb-6"
          >
            Gallery & Media
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto"
          >
            Witness the transformative power of compassion through authentic stories, real moments, and genuine impact from our community.
          </motion.p>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.1,
                  delayChildren: 0.6,
                },
              },
            }}
            className="grid grid-cols-3 gap-4 max-w-2xl mx-auto"
          >
            <motion.div
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0 },
              }}
              whileHover={{ scale: 1.05 }}
              className="bg-card rounded-lg p-3 sm:p-6 shadow-subtle"
            >
              <div className="text-2xl sm:text-3xl font-bold text-primary mb-2">{totalPhotos}+</div>
              <div className="text-xs sm:text-sm text-muted-foreground">Photos</div>
            </motion.div>
            <motion.div
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0 },
              }}
              whileHover={{ scale: 1.05 }}
              className="bg-card rounded-lg p-6 shadow-subtle"
            >
              <div className="text-2xl sm:text-3xl font-bold text-secondary mb-2">{totalVideos}+</div>
              <div className="text-xs sm:text-sm text-muted-foreground">Videos</div>
            </motion.div>
            <motion.div
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0 },
              }}
              whileHover={{ scale: 1.05 }}
              className="bg-card rounded-lg p-6 shadow-subtle"
            >
              <div className="text-2xl sm:text-3xl font-bold text-accent mb-2">{totalStories}+</div>
              <div className="text-xs sm:text-sm text-muted-foreground">Stories</div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default GalleryHero;